import {Card, DataStatus, Flag} from 'platform/components';
import {HStack, Icon} from 'platform/foundation';

import {ReactElement} from 'react';

import {uniq, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetConsentTypesQuery} from '@dms/api/customer';
import {ConsentChannel, ConsentTemplate, ConsentType} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {getLocaleLabel, useTenant} from '@dms/shared';
import {ColumnType, DataType, SimpleTable} from '@dms/teas';

import {buildArray, useNavigate} from 'shared/web';

import {ConsentTypeRecipient} from './ConsentTypeRecipient';
import {ConsentTypesListActions} from './ConsentTypesListActions';
import {TemplatesCount} from './TemplatesCount';

export function GdprConsentTypesList() {
  const navigate = useNavigate();

  return (
    <Card
      title={i18n.t('entity.gdpr.labels.consentTypes')}
      actions={[
        {
          'data-testid': 'gdpr-consents-create-button',
          onClick: () => navigate(settingsRoutes.gdprConsentTypesNew),
          title: i18n.t('general.labels.createNew'),
          leftIcon: 'content/add_circle',
          variant: 'link',
          type: 'button',
        },
      ]}
    >
      <GdprConsentTypesTable />
    </Card>
  );
}

function GdprConsentTypesTable() {
  const {data: consentTypes, isLoading, isError} = useGetConsentTypesQuery();
  const {isMultiLocale} = useTenant();

  const rows = [
    {
      data: (consentTypes ?? []).map((type: ConsentType): DataType => {
        const channels: Record<string, ReactElement> = {};
        type.channels.forEach((channel: ConsentChannel) => {
          channels[channel] = <Icon value="AV/fiber_manual_record" size={4} />;
        });

        return {
          ...channels,
          name: type.name,
          recipient: <ConsentTypeRecipient consentType={type} />,
          templates: <TemplatesCount templates={type.templates} />,
          ...(isMultiLocale ? {locales: <TemplateLocales templates={type.templates} />} : {}),
          action: <ConsentTypesListActions type={type} />,
        };
      }),
    },
  ];

  return (
    <DataStatus spacing={4} isEmpty={!consentTypes?.length} isLoading={isLoading} isError={isError}>
      <SimpleTable
        data-testid="gdpr-consents-list-table"
        tdPadding="default"
        columns={getColumns(isMultiLocale)}
        showRowDivider
        rows={rows}
        noZebra
      />
    </DataStatus>
  );
}

function TemplateLocales(props: {templates: ConsentTemplate[]}) {
  const locales = uniq(props.templates.map((t) => t.locale).filter(isNotNilOrEmpty)).filter(
    isNotNil
  );

  return (
    <HStack spacing={1} wrap>
      {locales.map((locale) => (
        <Flag
          key={locale}
          colorScheme="neutral"
          isSubtle
          label={getLocaleLabel(locale) ?? locale}
        />
      ))}
    </HStack>
  );
}

const getColumns = (isMultiLocale: boolean): ColumnType<DataType>[] =>
  buildArray<ColumnType<DataType>>()
    .add({Header: i18n.t('entity.gdpr.labels.consentTypeName'), id: 'name'})
    .add({Header: i18n.t('entity.email.labels.recipient'), id: 'recipient'})
    .add({Header: i18n.t('entity.gdpr.labels.templatesCount'), id: 'templates'})
    .when(isMultiLocale, {Header: i18n.t('general.labels.locales'), id: 'locales'})
    .add({Header: i18n.t('general.labels.phone'), id: 'phone'})
    .add({Header: i18n.t('general.labels.sms'), id: 'sms'})
    .add({Header: i18n.t('general.labels.email'), id: 'email'})
    .add({Header: i18n.t('general.labels.mail'), id: 'mail'})
    .add({Header: <></>, id: 'action', width: 32});
